import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import RequirePermission from "../../auth/RequirePermission";
import StatusBadge from "../../components/midpoint/intelligence/StatusBadge";
import { formatDateTime } from "../../components/midpoint/admin/users/adminUserFormatters";
import { requestJson } from "../../api/apiClient";

function toneForStatus(status) {
  if (status === "sent") {
    return "confirmed";
  }
  if (status === "failed") {
    return "disputed";
  }
  if (status?.startsWith("skipped")) {
    return "pending";
  }
  return "neutral";
}

function AdminNotificationEventsPageContent() {
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({
    deliveryStatus: "",
    eventType: "",
    clientViewId: "",
  });
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.deliveryStatus) {
      params.set("deliveryStatus", filters.deliveryStatus);
    }
    if (filters.eventType) {
      params.set("eventType", filters.eventType);
    }
    if (filters.clientViewId) {
      params.set("clientViewId", filters.clientViewId);
    }
    const body = await requestJson(`/api/notifications/events/admin?${params}`);
    setEvents(body.data || []);
    setPagination(body.pagination);
  }, [filters]);

  useEffect(() => {
    load().catch((error) => setMessage(error.message));
  }, [load]);

  const runEngine = async () => {
    setMessage("Running alert engine…");
    const body = await requestJson("/api/notifications/alert-engine/run", {
      method: "POST",
      body: JSON.stringify({ includeLegacy: true }),
    });
    setMessage(`Alert engine finished. Summary: ${JSON.stringify(body.summary)}`);
    await load();
  };

  const retryEvent = async (id) => {
    await requestJson(`/api/notifications/events/admin/${id}/retry`, { method: "POST" });
    await load();
  };

  const cancelEvent = async (id) => {
    await requestJson(`/api/notifications/events/admin/${id}/cancel`, { method: "POST" });
    await load();
  };

  return (
    <div className="midpoint-admin-page">
      <header className="panel-card">
        <p className="eyebrow">Admin</p>
        <h1>Notification events</h1>
        <p className="muted">Durable alert engine events across Client Views and accounts.</p>
        <div className="cv-studio-page__actions">
          <button type="button" className="report-submit-cta" onClick={runEngine}>
            Run alert engine
          </button>
          <Link className="midpoint-share-button" to="/">
            Back to map
          </Link>
        </div>
      </header>

      {message ? (
        <section className="panel-card">
          <p>{message}</p>
        </section>
      ) : null}

      <section className="panel-card">
        <div className="cv-studio-section__grid">
          <label>
            Status
            <select
              value={filters.deliveryStatus}
              onChange={(e) => setFilters({ ...filters, deliveryStatus: e.target.value })}
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="skipped_preferences">Skipped (preferences)</option>
              <option value="queued">Queued (digest)</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>
          <label>
            Event type
            <input
              value={filters.eventType}
              onChange={(e) => setFilters({ ...filters, eventType: e.target.value })}
              placeholder="saved_search_new_match"
            />
          </label>
          <label>
            Client View ID
            <input
              value={filters.clientViewId}
              onChange={(e) => setFilters({ ...filters, clientViewId: e.target.value })}
            />
          </label>
        </div>
        <button type="button" className="midpoint-share-button" onClick={load}>
          Apply filters
        </button>
      </section>

      <section className="panel-card">
        <table className="admin-data-health-table">
          <thead>
            <tr>
              <th>Created</th>
              <th>Type</th>
              <th>Status</th>
              <th>Subject</th>
              <th>Client View</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.notificationEventId}>
                <td>{formatDateTime(event.createdAt)}</td>
                <td>{event.eventType}</td>
                <td>
                  <StatusBadge label={event.deliveryStatus} tone={toneForStatus(event.deliveryStatus)} />
                </td>
                <td>{event.subject || event.preview || "—"}</td>
                <td>{event.clientViewId ? event.clientViewId.slice(0, 8) : "—"}</td>
                <td>
                  <button type="button" onClick={() => setSelected(event)}>
                    Detail
                  </button>
                  {event.deliveryStatus === "failed" ? (
                    <button type="button" onClick={() => retryEvent(event.notificationEventId)}>
                      Retry
                    </button>
                  ) : null}
                  {event.deliveryStatus === "pending" ? (
                    <button type="button" onClick={() => cancelEvent(event.notificationEventId)}>
                      Cancel
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pagination ? (
          <p className="muted">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} events)
          </p>
        ) : null}
      </section>

      {selected ? (
        <section className="panel-card">
          <h2>Event detail</h2>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem" }}>
            {JSON.stringify(selected, null, 2)}
          </pre>
          {selected.errorMessage ? <p className="cv-auth-form__error">{selected.errorMessage}</p> : null}
        </section>
      ) : null}
    </div>
  );
}

export default function AdminNotificationEventsPage() {
  return (
    <RequirePermission permission="notifications.manage">
      <AdminNotificationEventsPageContent />
    </RequirePermission>
  );
}
